import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Zap, Clock } from 'lucide-react';
import axios from '../lib/axios';
import { API } from '../App';

/**
 * Boost Config Context
 * Provides boost configuration throughout the app.
 * Fetches settings from admin backend to control Coming Soon overlay.
 */
const BoostConfigContext = createContext({
  enabled: false,
  packages: [],
  loading: true,
  refetch: () => {}
});

export const useBoostConfig = () => useContext(BoostConfigContext);

export const BoostConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    enabled: false,
    packages: [],
    loading: true
  });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/boost-config`);
      setConfig({
        enabled: response.data.enabled,
        packages: response.data.packages || [],
        currency: response.data.currency || 'XCG',
        isPaymentConfigured: response.data.is_payment_configured,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch boost config:', error);
      setConfig(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Refetch when window regains focus (catches admin updates in other tabs)
  useEffect(() => {
    const handleFocus = () => {
      fetchConfig();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchConfig]);

  return (
    <BoostConfigContext.Provider value={{ ...config, refetch: fetchConfig }}>
      {children}
    </BoostConfigContext.Provider>
  );
};

/**
 * ComingSoonOverlay - A reusable overlay for features that are coming soon
 * 
 * Used for the Boost functionality until Sentoo integration is complete.
 * Features glass-morphism styling with a semi-transparent blur effect.
 * 
 * When useBoostEnabled is true, it will check the admin config to determine
 * if the overlay should be shown.
 */
export const ComingSoonOverlay = ({ 
  children, 
  title = "Coming Soon",
  subtitle = "Boost your visibility",
  icon: Icon = Zap,
  showOverlay = true,
  useBoostEnabled = false, // If true, use boost config to determine overlay
  className = ""
}) => {
  const boostConfig = useBoostConfig();
  
  // Determine if overlay should show
  let shouldShowOverlay = showOverlay;
  
  if (useBoostEnabled) {
    // Use boost config: show overlay if boost is NOT enabled
    shouldShowOverlay = !boostConfig.enabled;
  }
  
  if (!shouldShowOverlay) {
    return children;
  }

  return (
    <div className={`relative ${className}`}>
      {/* The underlying content (disabled/blurred) */}
      <div className="pointer-events-none opacity-50 select-none">
        {children}
      </div>
      
      {/* Glass-morphism overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-background/60 dark:bg-background/70 backdrop-blur-[2px] rounded-xl">
        <div className="text-center px-4 py-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white mb-2 shadow-lg shadow-amber-500/20">
            <Icon className="w-5 h-5" />
          </div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * BoostButton - The button users click to boost their listing
 * Shows ComingSoonOverlay until Sentoo integration is ready
 */
export const BoostButton = ({ 
  listingId, 
  isEnabled = false, // Set to true when Sentoo is integrated
  size = "default", // "small", "default", "large"
  variant = "default", // "default", "outline", "ghost"
  className = ""
}) => {
  const sizeClasses = {
    small: "px-2.5 py-1.5 text-xs gap-1",
    default: "px-4 py-2 text-sm gap-1.5",
    large: "px-5 py-2.5 text-base gap-2"
  };

  const iconSizes = {
    small: "w-3 h-3",
    default: "w-4 h-4",
    large: "w-5 h-5"
  };

  const variantClasses = {
    default: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md shadow-amber-500/20",
    outline: "border-2 border-amber-500 text-amber-600 hover:bg-amber-500/10",
    ghost: "text-amber-600 hover:bg-amber-500/10"
  };

  const buttonContent = (
    <button
      disabled={!isEnabled}
      className={`
        inline-flex items-center justify-center font-medium rounded-lg transition-all
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        ${!isEnabled ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      data-testid={`boost-button-${listingId}`}
    >
      <Zap className={iconSizes[size]} />
      <span>Boost</span>
    </button>
  );

  if (!isEnabled) {
    return (
      <ComingSoonOverlay 
        title="Coming Soon" 
        subtitle="Boost visibility"
        showOverlay={true}
      >
        {buttonContent}
      </ComingSoonOverlay>
    );
  }

  return buttonContent;
};

/**
 * BoostCard - A card component for the Dashboard showing boost options
 */
export const BoostCard = ({ 
  listing,
  isEnabled = false 
}) => {
  const isBoosted = listing?.is_boosted;
  const boostExpiresAt = listing?.boost_expires_at;

  // If already boosted, show status
  if (isBoosted && boostExpiresAt) {
    const expiresDate = new Date(boostExpiresAt);
    const timeLeft = Math.max(0, expiresDate - new Date());
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
    const daysLeft = Math.floor(hoursLeft / 24);

    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg">
        <Zap className="w-4 h-4 text-amber-500" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Boosted</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {daysLeft > 0 ? `${daysLeft}d ${hoursLeft % 24}h left` : `${hoursLeft}h left`}
          </p>
        </div>
      </div>
    );
  }

  // Show boost option (with coming soon overlay if not enabled)
  return (
    <ComingSoonOverlay showOverlay={!isEnabled}>
      <div className="flex items-center gap-3 px-3 py-2 bg-muted/30 border border-border rounded-lg">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
          <Zap className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Boost this listing</p>
          <p className="text-xs text-muted-foreground">Get more visibility</p>
        </div>
        <button 
          disabled={!isEnabled}
          className="px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg"
        >
          Boost
        </button>
      </div>
    </ComingSoonOverlay>
  );
};

/**
 * BoostedBadge - Small badge to show on listing cards that are boosted
 */
export const BoostedBadge = ({ className = "" }) => {
  return (
    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-semibold rounded ${className}`}>
      <Zap className="w-2.5 h-2.5" />
      <span>BOOSTED</span>
    </div>
  );
};

export default ComingSoonOverlay;
